import { useCallback, useState } from 'react';
import type { CartItemDTO, ShoppingCartDTO } from '@t/api.types';
import { getNonCouponItems } from '@hooks/useShoppingCart/coupon';
import { getDeleteConstraint } from '@utils/cartUtils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type UseDeleteCartItemOptions = {
  cart: ShoppingCartDTO | null;
  onRemoveItem: (offeringId: string) => void;
  onRemoveAllItems: () => void;
};

type UseDeleteCartItemReturn = {
  handleDeleteItem: (item: CartItemDTO) => void;
  deleteInternetDialogOpen: boolean;
  closeDeleteInternetDialog: () => void;
  confirmDeleteInternet: () => void;
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useDeleteCartItem = ({
  cart,
  onRemoveItem,
  onRemoveAllItems,
}: UseDeleteCartItemOptions): UseDeleteCartItemReturn => {
  const [deleteInternetDialogOpen, setDeleteInternetDialogOpen] = useState(false);

  // Coupons are not plans: they must not count toward the internet-dependency constraint.
  const items = getNonCouponItems(cart);

  const handleDeleteItem = useCallback(
    (item: CartItemDTO) => {
      const offeringId = item.productOffering?.id;
      if (!offeringId) return;

      const constraint = getDeleteConstraint(item, items);
      if (constraint === 'internet-dependency') {
        setDeleteInternetDialogOpen(true);
        return;
      }
      if (constraint === 'internet-only') {
        onRemoveAllItems();
        return;
      }

      onRemoveItem(offeringId);
    },
    [items, onRemoveItem, onRemoveAllItems]
  );

  const confirmDeleteInternet = useCallback(() => {
    setDeleteInternetDialogOpen(false);
    onRemoveAllItems();
  }, [onRemoveAllItems]);

  const closeDeleteInternetDialog = useCallback(() => {
    setDeleteInternetDialogOpen(false);
  }, []);

  return {
    handleDeleteItem,
    deleteInternetDialogOpen,
    closeDeleteInternetDialog,
    confirmDeleteInternet,
  };
};
